import React, { useEffect, useRef, useState } from 'react';
import { track } from '../utils/analytics';
import { ReactComponent as InfoIcon } from '../assets/icons/info.svg';
import { ReactComponent as TipIcon } from '../assets/icons/tip.svg';
import { FLAC_SUPPORT_TIERS, FLAC_UNSUPPORTED_MODELS } from '../constants/flacCompatibility';
import './StatInfo.scss';

// Small ⓘ affordances pinned to the top-right of Compatibility summary stats,
// for the two statuses that name a problem:
// - Incompatible → plain tooltip (the app fixes these; nothing to decide)
// - Depends → tooltip + "See the full player list" CTA opening the per-model
//   FLAC reference modal, rendered from constants/flacCompatibility.js (the
//   same source the About tab uses).

// Shared icon-with-tooltip base. The tooltip renders position:fixed (coords
// from the button rect) because every ancestor of the stats row is
// overflow:hidden — and it hides on a short timer that entering either the
// icon or the tooltip cancels, so its CTA stays reachable.
const InfoIconTip = ({ variant, ariaLabel, onIconClick, cta, children }) => {
    const [tipPos, setTipPos] = useState(null);
    const hideTimer = useRef(null);

    const cancelHide = () => clearTimeout(hideTimer.current);
    const scheduleHide = () => {
        cancelHide();
        hideTimer.current = setTimeout(() => setTipPos(null), 200);
    };
    useEffect(() => cancelHide, []);

    return (
        <>
            <button
                type="button"
                className={`stat-info-btn ${variant}`}
                onClick={onIconClick}
                onMouseEnter={(e) => {
                    cancelHide();
                    const r = e.currentTarget.getBoundingClientRect();
                    setTipPos({ x: r.left + r.width / 2, y: r.top - 7 });
                }}
                onMouseLeave={scheduleHide}
                aria-label={ariaLabel}
            >
                <InfoIcon className="stat-info-icon" />
            </button>
            {tipPos && (
                <span
                    className="stat-info-tooltip"
                    role="tooltip"
                    style={{ left: tipPos.x, top: tipPos.y }}
                    onMouseEnter={cancelHide}
                    onMouseLeave={scheduleHide}
                >
                    {children}
                    {cta && (
                        <button
                            type="button"
                            className="tooltip-cta"
                            onClick={() => { setTipPos(null); cta.onClick(); }}
                        >
                            {cta.label}
                        </button>
                    )}
                </span>
            )}
        </>
    );
};

export const IncompatibleInfo = () => (
    <InfoIconTip variant="incompatible" ariaLabel="Why are these files incompatible?">
        WAV files with a non-standard header (common with Bandcamp downloads) &mdash;
        CDJs reject them with <span className="error-code">E-8305</span>. Fix Tracks
        repairs them in place without touching the audio.
    </InfoIconTip>
);

const DependsInfo = ({ count }) => {
    const [modalOpen, setModalOpen] = useState(false);
    const overlayRef = useRef(null);

    useEffect(() => {
        if (!modalOpen) return;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') setModalOpen(false);
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [modalOpen]);

    const openModal = () => {
        track('compatibility_depends_info_opened', { count });
        setModalOpen(true);
    };

    return (
        <>
            <InfoIconTip
                variant="depends"
                ariaLabel="What does Depends mean?"
                onIconClick={openModal}
                cta={{ label: 'See the full player list →', onClick: openModal }}
            >
                FLAC files &mdash; they play on newer players, but older CDJs reject
                them with <span className="error-code">E-8305</span>.
            </InfoIconTip>

            {modalOpen && (
                <div
                    className="flac-depends-overlay"
                    ref={overlayRef}
                    onClick={(e) => { if (e.target === overlayRef.current) setModalOpen(false); }}
                >
                    <div className="flac-depends-modal" role="dialog" aria-label="FLAC player compatibility">
                        <div className="fd-modal-header">
                            <span className="filetype-badge flac">FLAC</span>
                            <span className="fd-modal-title">Which players can play these files?</span>
                            <button type="button" className="fd-modal-close" onClick={() => setModalOpen(false)} aria-label="Close">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                            </button>
                        </div>
                        <div className="fd-modal-body">
                            <p className="fd-lede">
                                &ldquo;Depends&rdquo; marks FLAC files. They play on newer
                                Pioneer&nbsp;DJ / AlphaTheta players, but older ones reject them
                                with an <span className="error-code">E-8305</span> error at the
                                worst possible moment.
                            </p>
                            <div className="fd-section">
                                <div className="fd-heading plays">Plays on</div>
                                {FLAC_SUPPORT_TIERS.map(({ spec, models }) => (
                                    <div className="fd-tier" key={models[0]}>
                                        <span className="fd-chips">
                                            {models.map((m) => <span className="fd-chip plays" key={m}>{m}</span>)}
                                        </span>
                                        {spec && <span className="fd-spec">{spec}</span>}
                                    </div>
                                ))}
                            </div>
                            <div className="fd-section">
                                <div className="fd-heading fails">Won&rsquo;t play on</div>
                                <span className="fd-chips">
                                    {FLAC_UNSUPPORTED_MODELS.map((m) => <span className="fd-chip fails" key={m}>{m}</span>)}
                                </span>
                            </div>
                        </div>
                        <div className="fd-modal-footer">
                            <TipIcon className="fd-tip-icon" />
                            <span>
                                Playing on older gear? Turn on <strong>Convert FLAC files</strong> in
                                Settings and run Fix &mdash; they&rsquo;ll play everywhere.
                            </span>
                            <button type="button" className="fd-got-it" onClick={() => setModalOpen(false)}>
                                Got it
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default DependsInfo;
